import React, { useCallback, useEffect, useState } from 'react';
import { useAdmin } from 'src/admin';
import { ADMIN_DATA_CHANGED_EVENT } from 'src/admin/AdminContext';
import { IconClose, IconCheck, IconPencil, IconBell } from 'src/icons';
import {
  postAdminNotificationsApi,
  getAdminNotificationsSentApi,
  getAdminNotificationsIncomingApi,
  markAdminIncomingHandledApi,
  adminResetPasswordApi,
  getAdminUsersApi,
  type AdminIncomingItem,
} from 'src/services/api';

const STORAGE_USER = 'flower_power_notes_user';

interface SentItem {
  title: string;
  body: string;
  sentAt: string;
  recipientCount: number;
}

interface UserOption {
  id: number;
  email: string | null;
  phone: string | null;
  displayName: string | null;
}

type Tab = 'outgoing' | 'incoming';
type RecipientMode = 'all' | 'selected';
type SendMethod = 'inapp' | 'email' | 'phone';

const AdminNotifications: React.FC = () => {
  const { refreshAdminData } = useAdmin();
  const [activeTab, setActiveTab] = useState<Tab>('outgoing');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sendMethods, setSendMethods] = useState<SendMethod[]>(['inapp']);
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sent, setSent] = useState<SentItem[]>([]);
  const [incoming, setIncoming] = useState<AdminIncomingItem[]>([]);
  const [loadingSent, setLoadingSent] = useState(true);
  const [loadingIncoming, setLoadingIncoming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [handlingId, setHandlingId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resetPopupItem, setResetPopupItem] = useState<AdminIncomingItem | null>(null);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [recipientPanelOpen, setRecipientPanelOpen] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [stats, setStats] = useState<{ sentToday: number; totalDeliveries: number }>({
    sentToday: 0,
    totalDeliveries: 0,
  });

  const getAuthToken = () => {
    const raw = localStorage.getItem(STORAGE_USER);
    const user = raw ? JSON.parse(raw) : {};
    return user?.AuthToken ?? '';
  };

  const loadSent = useCallback(async () => {
    setLoadingSent(true);
    const result = await getAdminNotificationsSentApi(getAuthToken());
    setLoadingSent(false);
    if (result.error) return;
    setSent(result.sent ?? []);
    updateStats(result.sent ?? []);
  }, []);

  const loadIncoming = useCallback(async () => {
    setLoadingIncoming(true);
    const result = await getAdminNotificationsIncomingApi(getAuthToken());
    setLoadingIncoming(false);
    if (result.error) return;
    setIncoming(result.incoming ?? []);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    const result = await getAdminUsersApi(getAuthToken());
    setLoadingUsers(false);
    if (result.error) return;
    setUsers(
      (result.users ?? []).map((u) => ({
        id: u.id,
        email: u.email,
        phone: u.phone,
        displayName: u.displayName,
      }))
    );
  }, []);

  const updateStats = (sentNotifications: SentItem[]) => {
    const today = new Date().toDateString();
    const sentToday = sentNotifications.filter(n =>
      new Date(n.sentAt).toDateString() === today
    ).length;

    setStats({
      sentToday,
      totalDeliveries: sentNotifications.reduce((sum, n) => sum + n.recipientCount, 0),
    });
  };

  useEffect(() => {
    loadSent();
  }, [loadSent]);

  useEffect(() => {
    loadIncoming();
  }, [loadIncoming]);

  useEffect(() => {
    if (activeTab === 'outgoing') loadUsers();
  }, [activeTab, loadUsers]);

  useEffect(() => {
    const handler = () => {
      loadSent();
      loadIncoming();
      loadUsers();
    };
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
  }, [loadSent, loadIncoming, loadUsers]);

  const toggleUserSelection = (id: number) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const filteredUsers = recipientSearch.trim()
    ? users.filter((u) => {
        const q = recipientSearch.toLowerCase().trim();
        const name = (u.displayName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const phone = (u.phone || '').toLowerCase();
        const id = String(u.id);
        return name.includes(q) || email.includes(q) || phone.includes(q) || id.includes(q);
      })
    : users;

  const selectAllUsers = () => {
    setSelectedUserIds(filteredUsers.map((u) => u.id));
  };

  const clearUserSelection = () => {
    setSelectedUserIds([]);
  };

  const toggleSendMethod = (method: SendMethod) => {
    setSendMethods((prev) =>
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method]
    );
  };

  const handleSend = async () => {
    setError(null);
    if (!title.trim() || !body.trim()) {
      setError('Title and message are required');
      return;
    }
    if (sendMethods.length === 0) {
      setError('Select at least one send method (In-app, Email, or Phone)');
      return;
    }
    if (recipientMode === 'selected' && selectedUserIds.length === 0) {
      setError('Select at least one user or choose "Send to all users"');
      return;
    }
    setSending(true);
    const result = await postAdminNotificationsApi(getAuthToken(), {
      title: title.trim(),
      body: body.trim(),
      channels: sendMethods.filter((m) => m === 'inapp'),
      ...(recipientMode === 'all' ? { sendToAll: true } : { recipientIds: selectedUserIds }),
    });
    setSending(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setTitle('');
    setBody('');
    setMessage(`✅ Notification sent to ${result.count ?? 0} recipient(s)`);
    setTimeout(() => setMessage(null), 4000);
    loadSent();
    await refreshAdminData();
  };

  const handleMarkHandled = async (item: AdminIncomingItem) => {
    setHandlingId(item.id);
    const result = await markAdminIncomingHandledApi(getAuthToken(), item.id);
    setHandlingId(null);
    if (result.error) {
      setMessage('❌ Error: ' + result.error);
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    setMessage('✅ Request marked as handled');
    setTimeout(() => setMessage(null), 3000);
    loadIncoming();
    await refreshAdminData();
  };

  const openResetPopup = (item: AdminIncomingItem) => {
    setResetPopupItem(item);
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetError(null);
  };

  const closeResetPopup = () => {
    setResetPopupItem(null);
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetError(null);
  };

  const handleResetSubmit = async () => {
    if (!resetPopupItem) return;
    const newPassword = resetNewPassword.trim();
    const confirm = resetConfirmPassword.trim();
    setResetError(null);
    if (!newPassword) {
      setResetError('Enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirm) {
      setResetError('Passwords do not match');
      return;
    }
    setResetLoading(true);
    const resetResult = await adminResetPasswordApi(getAuthToken(), {
      userId: resetPopupItem.userId,
      newPassword,
    });
    if (resetResult.error) {
      setResetLoading(false);
      setResetError(resetResult.error);
      return;
    }
    const handledResult = await markAdminIncomingHandledApi(getAuthToken(), resetPopupItem.id);
    setResetLoading(false);
    if (handledResult.error) {
      setResetError('Password reset succeeded but marking as handled failed: ' + handledResult.error);
      return;
    }
    setMessage('✅ Password reset and request resolved');
    setTimeout(() => setMessage(null), 3000);
    await refreshAdminData();
    closeResetPopup();
    loadIncoming();
  };

  const incomingPending = incoming.filter((i) => !i.handledAt);
  const incomingHandled = incoming.filter((i) => i.handledAt);

  const formatDateTime = (dateStr: string | null) => {
    if (dateStr == null) return '–';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffHours < 1) {
        return `${Math.floor(diffHours * 60)}m ago`;
      } else if (diffHours < 24) {
        return `${Math.floor(diffHours)}h ago`;
      } else if (diffHours < 48) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return dateStr;
    }
  };

  const formatFullDateTime = (dateStr: string | null) => {
    if (dateStr == null) return '–';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex flex-col flex-1 p-1.5 min-h-0 overflow-hidden">
      <header className="bg-white px-1.5 py-1 border-gray-200 border-b shrink-0">
        <div className="flex justify-between items-center gap-1">
          <h1 className="font-semibold text-gray-900 text-sm truncate">Notifications</h1>
          <div className="flex items-center gap-1 text-gray-600 text-xs">
            <span>{sent.length} sent</span>
            <span>·</span>
            <span>{incomingPending.length} pending</span>
            {users.length > 0 && (
              <>
                <span>·</span>
                <span>{users.length} users</span>
              </>
            )}
            <span>·</span>
            <span>{stats.totalDeliveries} deliveries</span>
          </div>
        </div>
      </header>

      {(message || error) && (
        <div className="py-0.5 shrink-0">
          <div
            className={`rounded border px-1.5 py-1 text-xs ${
              error
                ? 'border-red-200 bg-gradient-to-r from-red-50/90 to-red-50/70 text-red-800'
                : 'border-emerald-200 bg-gradient-to-r from-emerald-50/90 to-emerald-50/70 text-emerald-800'
            }`}
            role="alert"
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`flex-shrink-0 w-4 h-4 rounded-full ${error ? 'bg-red-100' : 'bg-emerald-100'} flex items-center justify-center`}>
                  {error ? (
                    <IconClose className="w-2.5 h-2.5 text-red-500" />
                  ) : (
                    <IconCheck className="w-2.5 h-2.5 text-emerald-500" />
                  )}
                </div>
                <span className="font-medium truncate">{error || message}</span>
              </div>
              <button
                type="button"
                onClick={() => { setError(null); setMessage(null); }}
                className="ml-2 p-1 rounded text-gray-400 hover:text-gray-600 shrink-0"
                aria-label="Dismiss"
              >
                <IconClose className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="py-0.5 shrink-0">
        <div className="flex gap-0.5 bg-gray-100 p-0.5 border border-gray-200 rounded">
          <button
            type="button"
            onClick={() => setActiveTab('outgoing')}
            className={`flex-1 py-1 font-medium text-xs rounded transition-colors flex items-center justify-center gap-0.5 ${
              activeTab === 'outgoing'
                ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <IconPencil className="w-3 h-3" />
            <span className="truncate">Send</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('incoming')}
            className={`flex-1 py-1 font-medium text-xs rounded transition-colors flex items-center justify-center gap-0.5 ${
              activeTab === 'incoming'
                ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <IconBell className="w-3 h-3" />
            <span className="truncate">Requests</span>
            {incomingPending.length > 0 && (
              <span className="flex justify-center items-center bg-amber-500 rounded-full min-w-[12px] h-[12px] text-white text-xs">
                {incomingPending.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {activeTab === 'outgoing' && (
          <div className="flex-1 gap-2 grid lg:grid-cols-2 min-h-0" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
            <div className="flex flex-col bg-white p-2 border border-gray-200 rounded min-h-0 overflow-hidden">
              <div className="flex items-center gap-1 mb-2 pb-1 border-gray-200 border-b shrink-0">
                <h2 className="font-semibold text-gray-900 text-sm truncate">Compose</h2>
              </div>

              <div className="flex-1 min-h-0 overflow-auto">
              <form
                className="space-y-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
              >
                <div>
                  <label className="block font-medium text-gray-700 text-xs">Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Title"
                    className="mt-0.5 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 w-full text-xs"
                    maxLength={100}
                  />
                  <span className="text-gray-400 text-xs">{title.length}/100</span>
                </div>

                <div>
                  <label className="block font-medium text-gray-700 text-xs">Message *</label>
                  <textarea
                    rows={2}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Message..."
                    className="mt-0.5 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 w-full min-h-[48px] text-xs resize-y"
                    maxLength={500}
                  />
                  <span className="text-gray-400 text-xs">{body.length}/500</span>
                </div>

                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">Send method *</label>
                  <div className="gap-1 grid grid-cols-3">
                    <button type="button" onClick={() => toggleSendMethod('inapp')} className={`p-1 border rounded text-xs flex items-center justify-center gap-0.5 ${sendMethods.includes('inapp') ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>In-app</button>
                    <button type="button" disabled title="Not implemented yet" className="flex justify-center items-center gap-0.5 bg-gray-50 opacity-70 p-1 border border-gray-300 rounded text-gray-400 text-xs cursor-not-allowed">Email</button>
                    <button type="button" disabled title="Not implemented yet" className="flex justify-center items-center gap-0.5 bg-gray-50 opacity-70 p-1 border border-gray-300 rounded text-gray-400 text-xs cursor-not-allowed">Phone</button>
                  </div>
                  <p className="mt-0.5 text-[10px] text-gray-400">Email and Phone are read-only (not implemented yet).</p>
                </div>

                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">Recipients *</label>
                  <div className="gap-1 grid grid-cols-2">
                    <button type="button" onClick={() => { setRecipientMode('all'); setRecipientPanelOpen(false); }} className={`p-1 border rounded text-xs ${recipientMode === 'all' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:bg-gray-50'}`}>All</button>
                    <button type="button" onClick={() => setRecipientMode('selected')} className={`p-1 border rounded text-xs ${recipientMode === 'selected' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:bg-gray-50'}`}>Select</button>
                  </div>
                  {recipientMode === 'selected' && (
                    <button type="button" onClick={() => setRecipientPanelOpen(true)} className="hover:bg-gray-50 mt-1 px-2 py-1 border border-gray-300 rounded w-full text-xs text-left">
                      {selectedUserIds.length === 0 ? 'Choose users…' : `${selectedUserIds.length} selected`}
                    </button>
                  )}
                </div>

                <button type="submit" disabled={sending || !title.trim() || !body.trim() || sendMethods.length === 0 || (recipientMode === 'selected' && selectedUserIds.length === 0)} className={`w-full py-1 rounded font-medium text-xs ${sending || !title.trim() || !body.trim() || sendMethods.length === 0 || (recipientMode === 'selected' && selectedUserIds.length === 0) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                  {sending ? '…' : 'Send'}
                </button>
              </form>
              </div>
            </div>

            <div className="flex flex-col bg-white p-2 border border-gray-200 rounded min-h-0 overflow-hidden">
              <div className="flex justify-between items-center mb-1 pb-1 border-gray-200 border-b shrink-0">
                <h2 className="font-semibold text-gray-900 text-sm truncate">Sent ({sent.length})</h2>
              </div>

              <div className="flex flex-col flex-1 min-h-0">
                {loadingSent ? (
                  <div className="flex flex-1 justify-center items-center py-2">
                    <span className="text-gray-500 text-xs">Loading…</span>
                  </div>
                ) : sent.length === 0 ? (
                  <div className="flex flex-1 justify-center items-center py-2 text-center">
                    <p className="text-gray-500 text-xs">No sent</p>
                  </div>
                ) : (
                  <div className="flex-1 -mr-0.5 pr-0.5 min-h-0 overflow-y-auto">
                    <div className="space-y-1">
                      {sent.map((n, i) => (
                        <div key={i} className="p-1.5 border border-gray-200 rounded">
                          <div className="flex justify-between items-start gap-1">
                            <span className="font-medium text-gray-900 text-xs truncate">{n.title}</span>
                            <span className="text-blue-600 text-xs shrink-0">{n.recipientCount}</span>
                          </div>
                          <p className="text-gray-600 text-xs truncate line-clamp-1">{n.body}</p>
                          <span className="text-gray-500 text-xs">{formatDateTime(n.sentAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'incoming' && (
          <div className="flex flex-col flex-1 bg-white p-2 border border-gray-200 rounded min-h-0 overflow-hidden">
            <div className="flex justify-between items-center mb-1 shrink-0">
              <h2 className="font-semibold text-gray-900 text-sm">Reset requests</h2>
              <span className="text-gray-500 text-xs">{incomingPending.length} pending / {incomingHandled.length} handled</span>
            </div>
            {loadingIncoming ? (
              <div className="flex flex-1 justify-center items-center py-2">
                <span className="text-gray-500 text-xs">Loading…</span>
              </div>
            ) : incoming.length === 0 ? (
              <div className="flex flex-1 justify-center items-center py-2 text-center">
                <p className="text-gray-500 text-xs">No requests</p>
              </div>
            ) : (
              <div className="flex-1 space-y-1 -mr-0.5 pr-0.5 min-h-0 overflow-y-auto">
                {incomingPending.length > 0 && incomingPending.map((item) => (
                  <div
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openResetPopup(item)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openResetPopup(item); } }}
                    className="bg-amber-50 p-1.5 border border-amber-200 hover:border-amber-300 rounded cursor-pointer"
                  >
                    <div className="flex justify-between items-center gap-1">
                      <span className="font-medium text-gray-900 text-xs truncate">{item.displayName || item.email || item.phone || `#${item.userId}`}</span>
                      <span className="text-amber-700 text-xs shrink-0">#{item.userId}</span>
                    </div>
                    <div className="flex justify-between items-center mt-0.5">
                      <span className="text-gray-500 text-xs">{formatDateTime(item.requestedAt)}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleMarkHandled(item); }} disabled={!!handlingId} className="bg-gray-500 hover:bg-gray-600 disabled:opacity-50 px-1 py-0.5 rounded text-white text-xs">
                        {handlingId === item.id ? '…' : 'Dismiss'}
                      </button>
                    </div>
                  </div>
                ))}
                {incomingHandled.length > 0 && incomingHandled.map((item) => (
                  <div key={item.id} className="flex justify-between items-center bg-gray-50 p-1.5 border border-gray-200 rounded">
                    <span className="font-medium text-gray-900 text-xs truncate">{item.displayName || item.email || item.phone || `#${item.userId}`}</span>
                    <span className="text-gray-500 text-xs">{formatDateTime(item.handledAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {resetPopupItem && (
        <>
          <div
            className="z-40 fixed inset-0 bg-black/50"
            aria-hidden
            onClick={closeResetPopup}
          />
          <div className="z-50 fixed inset-0 flex justify-center items-center p-2" role="dialog" aria-labelledby="reset-popup-title">
            <div className="bg-white shadow-xl p-3 border border-gray-200 rounded-lg w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
              <h2 id="reset-popup-title" className="mb-1 font-semibold text-gray-900 text-xs">Reset password</h2>
              <p className="mb-2 text-gray-500 text-xs truncate">{resetPopupItem.displayName || resetPopupItem.email || resetPopupItem.phone || `User #${resetPopupItem.userId}`}</p>
              <div className="space-y-1.5 mb-2">
                <label htmlFor="reset-new-password" className="block font-medium text-gray-700 text-xs">New password</label>
                <input id="reset-new-password" type="password" value={resetNewPassword} onChange={(e) => setResetNewPassword(e.target.value)} className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-sky-500 w-full text-xs" placeholder="Min 6" autoComplete="new-password" />
                <label htmlFor="reset-confirm-password" className="block font-medium text-gray-700 text-xs">Confirm</label>
                <input id="reset-confirm-password" type="password" value={resetConfirmPassword} onChange={(e) => setResetConfirmPassword(e.target.value)} className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-sky-500 w-full text-xs" placeholder="Re-enter" autoComplete="new-password" />
                {resetError && <p className="text-red-600 text-xs">{resetError}</p>}
              </div>
              <div className="flex flex-wrap gap-1">
                <button type="button" onClick={handleResetSubmit} disabled={resetLoading} className="flex-1 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 px-2 py-1 rounded font-medium text-white text-xs">{resetLoading ? '…' : 'Reset & resolve'}</button>
                <button type="button" onClick={closeResetPopup} className="hover:bg-gray-50 px-2 py-1 border border-gray-300 rounded font-medium text-gray-700 text-xs">Cancel</button>
                <button type="button" onClick={async () => { await handleMarkHandled(resetPopupItem); closeResetPopup(); }} disabled={!!handlingId} className="hover:bg-gray-100 disabled:opacity-50 px-2 py-1 rounded font-medium text-gray-600 text-xs">Dismiss</button>
              </div>
            </div>
          </div>
        </>
      )}

      {recipientPanelOpen && (
        <>
          <div
            className="z-40 fixed inset-0 bg-black/40"
            aria-hidden
            onClick={() => {
              setRecipientPanelOpen(false);
              setRecipientSearch('');
            }}
          />
          <aside className="top-0 right-0 bottom-0 z-50 fixed flex flex-col bg-white shadow-xl w-full max-w-xs" aria-label="Select recipients">
            <div className="flex justify-between items-center px-2 py-1.5 border-gray-200 border-b shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm">Select recipients</h3>
              <button type="button" onClick={() => { setRecipientPanelOpen(false); setRecipientSearch(''); }} className="hover:bg-gray-100 p-1 rounded text-gray-500 text-xs" aria-label="Close">✕</button>
            </div>
            <div className="p-1.5 border-gray-100 border-b shrink-0">
              <input type="search" value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} placeholder="Search…" className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 w-full text-xs" aria-label="Search users" autoFocus />
            </div>
            <div className="flex gap-1 px-1.5 py-1 border-gray-100 border-b shrink-0">
              <button type="button" onClick={selectAllUsers} className="hover:bg-blue-50 px-1.5 py-0.5 rounded font-medium text-blue-600 text-xs">All</button>
              <button type="button" onClick={clearUserSelection} className="hover:bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-500 text-xs">Clear</button>
              <span className="ml-auto text-gray-500 text-xs">{selectedUserIds.length} selected</span>
            </div>
            <div className="flex-1 py-0.5 min-h-0 overflow-auto">
              {loadingUsers ? (
                <div className="flex justify-center items-center py-4"><span className="text-gray-500 text-xs">Loading…</span></div>
              ) : filteredUsers.length === 0 ? (
                <div className="flex justify-center items-center py-4"><span className="text-gray-500 text-xs">{recipientSearch.trim() ? 'No match' : 'No users'}</span></div>
              ) : (
                <div>
                  {filteredUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-1.5 hover:bg-gray-50 px-2 py-1 border-gray-100 border-b last:border-b-0 cursor-pointer">
                      <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUserSelection(u.id)} className="border-gray-300 rounded w-3 h-3 text-blue-600" aria-label={`Select ${u.displayName || u.email || u.phone || u.id}`} />
                      <span className="flex-1 font-medium text-gray-900 text-xs truncate">{u.displayName || u.email || u.phone || `#${u.id}`}</span>
                      <span className="text-gray-400 text-xs shrink-0">#{u.id}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="p-1.5 border-gray-200 border-t shrink-0">
              <button type="button" onClick={() => { setRecipientPanelOpen(false); setRecipientSearch(''); }} className="bg-blue-600 hover:bg-blue-700 py-1 rounded w-full font-medium text-white text-xs">Done</button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default AdminNotifications;
